import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    OutlinedInput,
    Alert,
    Snackbar,
    Button
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import DrawerLayout from '../components/containers/drawerLayout/DrawerLayout';
import ContentContainer from '../components/containers/ContentContainer';
import LoaderCircular from '../components/LoaderCircular'
import { formatNumberToEuroString } from '../utils/FormatToPrice'
import { modifyProject } from '../services/database/Projects';
import { updateProject } from '../redux/reducers/ProjectsSlice'

const ModifyProject = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { projectId } = useParams();

    const allProjects = useSelector(state => state.projects)
    const [project, setProject] = useState(undefined)

    const [name, setName] = useState('');
    const [nameErrorMessage, setNameErrorMessage] = useState('');
    const haveNameError = nameErrorMessage.length !== 0;
    const lengthOfNameCharacters = name.split(' ').join('').length;

    const [description, setDescription] = useState('');

    const [solde, setSolde] = useState(0);
    const [soldeIsFocus, setSoldeIsFocus] = useState(false);
    const [soldeFormatEur, setSoldeFormatEur] = useState('0');

    const [formIsValid, setFormIsValid] = useState(false);
    const [buttonLoader, setButtonLoader] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);

    useEffect(() => {
        if (allProjects.length !== 0) {
            const projectIndex = allProjects.findIndex(
                (project) => project.projectId === projectId
            )

            if (projectIndex !== -1) {
                setProject(allProjects[projectIndex])
            } else {
                navigate('/')
            }
        }
    }, [allProjects, navigate, projectId])

    useEffect(() => {
        if (project !== undefined) {
            setName(project.title)
            setDescription(project.description)
            setSolde(project.solde)
            setSoldeFormatEur(formatNumberToEuroString(project.solde))
        }
    }, [project])

    const handleSoldeInput = (inputNumber) => {
        const soldeMin = 0;
        const soldeMax = 1000000000;

        if (inputNumber >= soldeMin && inputNumber <= soldeMax) {
            setSolde(inputNumber)
        }
    }

    const handleCheckNameValidity = () => {
        if (lengthOfNameCharacters === 0) {
            setNameErrorMessage('Le nom du projet est obligatoire.');
        } else {
            setNameErrorMessage('');
        }
    }

    const handleFormatSolde = () => {

        let soldeReformat = Number(solde.toString());

        const soldeIsNotInteger = soldeReformat.toString().indexOf(".") !== -1;

        if (soldeIsNotInteger) {
            soldeReformat = soldeReformat.toFixed(2)
        }

        let soldeFormatString = new Intl.NumberFormat("fr-FR").format(soldeReformat);

        setSoldeFormatEur(soldeFormatString)
        setSolde(soldeReformat)

    }

    const handleCheckFormValidity = () => {

        const formHaveModification = name !== project.title || description !== project.description || solde !== project.solde;

        if (lengthOfNameCharacters !== 0 && formHaveModification) {
            setFormIsValid(true)
        } else if (formIsValid) {
            setFormIsValid(false)
        }
    }

    const handleFormSubmit = async () => {
        setButtonLoader(true)

        await modifyProject(projectId, name, description, solde)
            .then(() => {
                dispatch(updateProject({
                    projectId: projectId,
                    title: name,
                    description: description,
                    solde: solde,
                }))
                goToProject()
            })
            .catch(() => {
                setButtonLoader(false)
                setOpenSnackbar(true)
            })
    }

    const goToProject = () => {
        navigate(`/project/${projectId}`)
    }

    if (project === undefined) {
        return (
            <LoaderCircular open={true} />
        )
    }

    return (
        <>
            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
                <Alert onClose={() => setOpenSnackbar(false)} severity='error' sx={{ width: '100%' }}>
                    Une erreur est survenue, merci de réessayer.
                </Alert>
            </Snackbar>

            <DrawerLayout headerTitle={`${project.title} / Modifier ce projet`} projectId={projectId}>
                <ContentContainer>
                    <Typography variant='h1' sx={{ marginBottom: 4, fontSize: { xs: 40, md: 48 } }} fontWeight='bold'>
                        Modifier ce projet
                    </Typography>

                    <TextField
                        autoFocus
                        required
                        inputProps={{ maxLength: 25 }}
                        type='text'
                        sx={{ m: 1, width: '100%' }}
                        value={name} label='Nom du projet'
                        onChange={e => setName(e.target.value)}
                        onBlur={() => {
                            handleCheckNameValidity()
                            handleCheckFormValidity()
                        }}
                        error={haveNameError}
                        helperText={haveNameError ? nameErrorMessage : `${name.length}/25`}
                    />

                    <TextField
                        multiline
                        maxRows={10}
                        inputProps={{ maxLength: 300 }}
                        type='text'
                        sx={{ m: 1, width: '100%' }}
                        value={description} label='Description'
                        onChange={e => setDescription(e.target.value)}
                        onBlur={() => {
                            handleCheckFormValidity()
                        }}
                        helperText={`${description.length}/300`}
                    />

                    <FormControl fullWidth sx={{ m: 1 }}>
                        <InputLabel htmlFor="outlined-adornment-amount">Solde actuel</InputLabel>
                        <OutlinedInput
                            id="outlined-adornment-amount"
                            type={soldeIsFocus ? 'number' : 'text'}
                            value={soldeIsFocus ? solde : soldeFormatEur}
                            onChange={e => handleSoldeInput(e.target.value)}
                            startAdornment={<InputAdornment position="start">€</InputAdornment>}
                            label="Solde actuel"
                            onFocus={() => setSoldeIsFocus(true)}
                            onBlur={() => {
                                setSoldeIsFocus(false)
                                handleFormatSolde()
                                handleCheckFormValidity()
                            }}
                        />
                    </FormControl>

                    <LoadingButton
                        loading={buttonLoader}
                        disabled={!formIsValid}
                        sx={{ width: '100%', height: 56, marginTop: 4 }}
                        variant='contained'
                        onClick={() => handleFormSubmit()}
                        type='submit'
                    >
                        Modifier
                    </LoadingButton>
                    <Button
                        variant='outlined'
                        sx={{ width: '100%', height: 56, marginTop: 2 }}
                        onClick={() => goToProject()}
                    >
                        Annuler
                    </Button>
                </ContentContainer>
            </DrawerLayout>
        </>
    )
}

export default ModifyProject;